import { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';
import { clsx } from 'clsx';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import { KpiStrip } from './KpiStrip';
import { PersonaTimeChart } from './PersonaTimeChart';
import { FitVsPerformanceScatter } from './FitVsPerformanceScatter';
import { PersonaSteeringMatrix } from './PersonaSteeringMatrix';
import { CreativeFormulasSection } from './CreativeFormulasSection';
import { HookPerformanceChart } from './HookPerformanceChart';
import { FormulaAnalysisView } from './FormulaAnalysisView';
import { CustomerQualityView } from './CustomerQualityView';
import { CreativeFatigueView } from './CreativeFatigueView';
import { CampaignPerformanceView } from './CampaignPerformanceView';
import { FunnelAttributionView } from './FunnelAttributionView';
import { DrillDownsRail } from './DrillDownsRail';
import { PersonaLanesRail } from './PersonaLanesRail';
import { AlignmentOpportunityBand } from './AlignmentOpportunityBand';
import { AdDetailDrawer } from './AdDetailDrawer';
import { PersonaDetailView } from './PersonaDetailView';
import { SteeringInvestigationView } from './SteeringInvestigationView';
import { ReviewedCoverageView } from './ReviewedCoverageView';
import { EmbeddedChatPanel, type ChatSeedInput } from './EmbeddedChatPanel';

export function PerformanceView() {
  const { clientUid } = useCreativeWorkbench();
  const location = useLocation();
  const params = useParams<{ personaId?: string }>();

  // Route the sub-views off the same component so the layout stays consistent.
  if (location.pathname.includes('/performance/persona/')) {
    return <PersonaDetailView />;
  }
  if (location.pathname.endsWith('/performance/steering')) {
    return <SteeringInvestigationView />;
  }
  if (location.pathname.endsWith('/performance/coverage')) {
    return <ReviewedCoverageView />;
  }
  if (location.pathname.endsWith('/performance/formulas')) {
    return <FormulaAnalysisView />;
  }
  if (location.pathname.endsWith('/performance/quality')) {
    return <CustomerQualityView />;
  }
  if (location.pathname.endsWith('/performance/fatigue')) {
    return <CreativeFatigueView />;
  }
  if (location.pathname.endsWith('/performance/campaigns')) {
    return <CampaignPerformanceView />;
  }
  if (location.pathname.endsWith('/performance/funnel')) {
    return <FunnelAttributionView />;
  }
  if (params.personaId) {
    return <PersonaDetailView />;
  }

  if (!clientUid) {
    return (
      <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl p-10 text-center">
        <TrendingUp size={24} className="mx-auto text-slate-400 mb-3" />
        <p className="text-sm text-slate-500">
          Pick a client in the header to inspect performance.
        </p>
      </div>
    );
  }

  return <PerformanceDashboard />;
}

function PerformanceDashboard() {
  const [chatSeed, setChatSeed] = useState<ChatSeedInput | null>(null);
  const [drawerAdId, setDrawerAdId] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp size={18} className="text-teal-500" />
          <h1 className="text-xl font-bold text-navy-900">Creative Performance</h1>
        </div>
        <p className="text-[11px] text-slate-500 italic max-w-xl text-right hidden md:block">
          Persona-first performance: which creative is steering Meta correctly,
          and how that steering pays back in lifetime value.
        </p>
      </header>

      <KpiStrip
        onTileClick={(tile) => {
          // Map KPI tile → analytics chat seed. Picks the closest supported
          // metric for the analytics router's persona-time-series view.
          const metricMap: Record<string, string> = {
            ltv_weighted_roas: 'ltv_roas',
            aligned_roas_lift: 'roas',
            aligned_spend_pct: 'alignment',
            steering_accuracy: 'steering',
            reviewed_coverage: 'reviewed_coverage',
            captured_incremental_revenue: 'alignment',
            revenue_upside: 'alignment',
          };
          const metric = metricMap[tile.key] ?? 'roas';
          setChatSeed({
            question: `Explain my ${tile.label} (currently ${tile.value}${tile.format === 'percent' ? '%' : ''}) and how it's trending.`,
            chart_axis: { metric, chart_type: 'area', date_range_days: 30 },
          });
        }}
      />

      <AlignmentOpportunityBand onDiagnose={setChatSeed} />

      <DrillDownsRail />

      <div className="grid grid-cols-12 gap-4">
        <div
          className={clsx(
            chatSeed ? 'col-span-12 lg:col-span-8' : 'col-span-12 lg:col-span-9',
            'space-y-4',
          )}
        >
          <PersonaTimeChart onDrillDown={setChatSeed} />

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <FitVsPerformanceScatter
              onAdSelect={(ad) => setDrawerAdId(ad.ad_id)}
              onDrillDown={setChatSeed}
            />
            <PersonaSteeringMatrix onDrillDown={setChatSeed} />
          </div>

          <CreativeFormulasSection onDrillDown={setChatSeed} />
        </div>

        <aside
          className={clsx(
            chatSeed ? 'col-span-12 lg:col-span-4' : 'col-span-12 lg:col-span-3',
            'space-y-4',
          )}
        >
          <PersonaLanesRail onAskAboutLane={setChatSeed} />
          <HookPerformanceChart />
          {chatSeed && (
            <EmbeddedChatPanel
              seed={chatSeed}
              onClose={() => setChatSeed(null)}
            />
          )}
        </aside>
      </div>

      {drawerAdId && (
        <AdDetailDrawer
          adId={drawerAdId}
          onClose={() => setDrawerAdId(null)}
          onDrillDown={setChatSeed}
        />
      )}
    </div>
  );
}
